import { debounce } from "es-toolkit/compat";
import { useEffect, useMemo, useRef, useState } from "react";

interface DebounceOptions {
  wait?: number;
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
}

type noop = (...args: any) => any;

function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;

  return ref;
}

export function useDebounceFn<T extends noop>(
  fn: T,
  options?: DebounceOptions
) {
  if (process.env.NODE_ENV === "development") {
    if (typeof fn !== "function") {
      console.error(
        `useDebounceFn expected parameter is a function, got ${typeof fn}`
      );
    }
  }

  const fnRef = useLatest(fn);

  const wait = options?.wait ?? 1000;
  const leading = options?.leading ?? false;
  const trailing = options?.trailing ?? true;
  const maxWait = options?.maxWait;

  const debounced = useMemo(
    () =>
      debounce<T>(
        ((...args: any[]) => {
          return fnRef.current(...args);
        }) as T,
        wait,
        {
          leading,
          trailing,
          ...(maxWait === undefined ? {} : { maxWait }),
        }
      ),
    [fnRef, wait, leading, trailing, maxWait]
  );

  useEffect(() => () => debounced.cancel(), [debounced]);

  return {
    run: debounced as unknown as T,
    cancel: () => debounced.cancel(),
    flush: () => debounced.flush(),
  };
}

export function useDebounce<T>(value: T, options?: DebounceOptions) {
  const [debounced, setDebounced] = useState(value);

  const { run } = useDebounceFn(() => {
    setDebounced(value);
  }, options);

  useEffect(() => {
    run();
  }, [value, run]);

  return debounced;
}
